import React, { useRef, useState } from "react";

import { API } from "aws-amplify";

import {
  Backdrop,
  CircularProgress,
  DialogContentText,
  makeStyles,
} from "@material-ui/core";

import { Confirm, SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import {
  findExistingUserByEmail,
  performCreateCarrierUserJunction,
  performUpdateUserReloadCarrier,
  sanitizeCarrierUser,
} from "../../../graphql/carrier/carrierCRUD";

import { useUser } from "../../../authenticatorApp/UserContext";
import { logError } from "../../../utils/appUtils";
import {
  carrierCompanyJunctions,
  getCarrierUserAcl,
  getCarrierYardOptions,
  hasCarrierLogin,
  planCarrierUserYards,
} from "../../../utils/carrierUserUtils";
import { UserStatus, UserType } from "../../../utils/constants";

import { emailCarrierInviteHTML } from "../../../managerApp/resources/carriers/users/emailCarrierInviteHTML";
import { emailInviteHTML } from "../../../managerApp/resources/carriers/users/emailInviteHTML";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

// The yards named back to the user, from the ids the form holds.
const namesFor = (reloadYardIds, reloadYards) =>
  reloadYardIds
    .map(
      (id) =>
        reloadYards.find((yard) => yard.id === id)?.name ?? "the transload",
    )
    .join(", ");

export const ValidateCreateButton = (props) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const { appUser } = useUser();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [existingUser, setExistingUser] = useState(null);

  // React Admin snapshots the onSuccess prop into a ref on the first click —
  // the one that only opens the confirmation — so the callback that eventually
  // runs the create is the closure from before the plan existed. A ref is what
  // the plan can be read back out of at that point; state cannot.
  const planRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const businessName = appUser?.reloadYardCarrier?.businessName;

  // Every membership under the company this session acts for, which is the pool
  // an invite may grant from — not the user's whole junction set. A dispatcher
  // whose junctions span two companies invites for the one they are acting as,
  // and the junction stream applies the same rule to the colleagues it cascades.
  const inviterJunctions = carrierCompanyJunctions(
    appUser?.reloadYardCarriers?.items,
    { businessName, carrierId: appUser?.carrierId },
  );

  // A colleague joining a company that works four transloads works all four —
  // that is what hiring them means — so the invite grants every transload the
  // inviter holds rather than asking which. Taking one away is the transload's
  // own decision (its manager blocks the user there); from here the choice is
  // between working with this company and not.
  //
  // Read from the inviter's memberships at save time rather than carried on the
  // form: a transload withdrawn while the form was open is gone from the pool,
  // and the plan reports it as unavailable instead of trying to grant it.
  const grantYardIds = getCarrierYardOptions(
    inviterJunctions,
    appUser?.appType,
  ).map((yard) => yard.id);

  const handleValidateSave = async () => {
    const formState = form.getState();
    const formData = formState.values;

    if (formState.invalid) {
      // Submit anyway: it never reaches the server while the form is invalid,
      // and it is what marks the untouched fields so they say what is missing.
      props.handleSubmitWithRedirect();
      return;
    }

    setIsSaving(true);

    try {
      const { carrierUser, customer, yardUser } = await findExistingUserByEmail(
        formData.email,
      );

      // A yard's own staff member or a customer contact holds this address.
      // Nothing here can be added to it: one address is one account.
      if (customer || yardUser) {
        notify("User with this email already exists.", "error");
        setIsSaving(false);
        return;
      }

      // Each granted yard writes that yard's own membership — the row is cut
      // from the inviter's junction there. The session's carrierId only breaks
      // the tie where two carrier entries at one yard both hold the inviter.
      const yardPlan = planCarrierUserYards(
        inviterJunctions,
        carrierUser?.reloadYardCarriers?.items,
        grantYardIds,
        { carrierId: appUser?.carrierId },
      );

      // A name atlas wider than the plan's usable pool: a yard that just
      // became unavailable — access withdrawn or blocked since the form was
      // drawn — still needs naming in the message that says it cannot be
      // granted.
      const reloadYards = (inviterJunctions ?? [])
        .map((junction) => junction.reloadYard)
        .filter(Boolean);

      if (yardPlan.unavailable.length) {
        notify(
          `You no longer have access to ${namesFor(
            yardPlan.unavailable,
            reloadYards,
          )}, so it cannot be granted. Please refresh and try again.`,
          "warning",
        );
      }

      if (!yardPlan.create.length && !yardPlan.reactivate.length) {
        // A pending invitee who already holds every ticked yard is the trace
        // of an invite whose email never went out: the junctions committed,
        // the send failed, and this save is the retry. The empty plan is
        // exactly right — nothing to write, only the invitation to deliver —
        // so it goes to confirmation as a resend instead of dead-ending here.
        if (
          carrierUser?.status === UserStatus.PENDING_REG &&
          yardPlan.alreadyMember.length
        ) {
          planRef.current = yardPlan;
          setExistingUser(carrierUser);
          setMessage(
            <DialogContentText>
              {carrierUser.firstName} {carrierUser.lastName} already has access
              to {namesFor(yardPlan.alreadyMember, reloadYards)}, but has not
              registered yet.
              <br />
              Confirm to resend their invitation.
              <br />
            </DialogContentText>,
          );
          setOpen(true);
          return;
        }

        notify(
          yardPlan.alreadyMember.length
            ? `${
                carrierUser?.firstName ?? formData.firstName
              } already works with ${namesFor(
                yardPlan.alreadyMember,
                reloadYards,
              )}.`
            : "There is nothing to grant this user.",
          "warning",
        );
        setIsSaving(false);
        return;
      }

      const granted = namesFor(
        [
          ...yardPlan.create.map((yard) => yard.reloadYardId),
          ...yardPlan.reactivate.map((yard) => yard.reloadYardId),
        ],
        reloadYards,
      );

      // An existing account keeps its own name and phone number — they are that
      // person's to change, not an inviter's — so the dialog names the account
      // rather than what was typed, and says so when the two differ.
      const typedDetailsIgnored =
        Boolean(carrierUser) &&
        (carrierUser.firstName !== formData.firstName ||
          carrierUser.lastName !== formData.lastName ||
          (Boolean(formData.phone) && carrierUser.phone !== formData.phone));

      planRef.current = yardPlan;
      setExistingUser(carrierUser ?? null);
      setMessage(
        <DialogContentText>
          {carrierUser ? (
            <>
              {carrierUser.firstName} {carrierUser.lastName} already has an
              account on transloads.co.
              <br />
              They will be given access to {granted}.
              <br />
              {typedDetailsIgnored && (
                <>
                  <br />
                  The name and phone number on their account stay as they are —
                  what you typed here is not applied to it.
                  <br />
                </>
              )}
            </>
          ) : (
            <>
              {formData.firstName} will be invited to {granted} as a dispatch
              user.
              <br />
              Once saved, you will not be able to change their email address.
              <br />
            </>
          )}
          {yardPlan.alreadyMember.length > 0 && (
            <>
              <br />
              They already work with{" "}
              {namesFor(yardPlan.alreadyMember, reloadYards)}.
              <br />
            </>
          )}
        </DialogContentText>,
      );
      setOpen(true);
    } catch (error) {
      setIsSaving(false);
      logError(
        "carrierApp:carrierUsers:ValidateCreateButton:handleValidateSave",
        error,
        notify,
      );
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setIsSaving(false);
    // Nothing was agreed to, so nothing stays agreed to. The next attempt
    // re-plans before it can submit.
    planRef.current = null;
  };

  const handleDialogConfirm = async () => {
    setOpen(false);

    // An existing account needs no CarrierUser record — only the junctions that
    // say which yards it now reaches — so it skips the resource create entirely.
    if (existingUser) await onSuccess({ data: existingUser, isExisting: true });
    else props.handleSubmitWithRedirect();
  };

  const transform = (data) => {
    data = { ...data };
    data.status = UserStatus.PENDING_REG;
    data.email = data.email.toLowerCase();
    return sanitizeCarrierUser(data);
  };

  const onSuccess = async ({ data, isExisting }) => {
    try {
      // The plan is what the confirmed dialog agreed to. Nothing reaches here
      // without it, and writing junctions the user never saw described would be
      // worse than saying so.
      const plan = planRef.current;
      if (!plan) throw new Error("No transload access was confirmed");

      // Invitees always join as dispatch: they are the only carrier users who
      // sign in to this portal, and it is the only kind of user a dispatcher can
      // create.
      const userFields = {
        carrierUserId: data.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        type: UserType.CARRIER_DISPATCH,
        acl: getCarrierUserAcl(UserType.CARRIER_DISPATCH),
      };

      const grants = [
        ...plan.create.map((yardFields) => ({
          reloadYardId: yardFields.reloadYardId,
          run: () =>
            performCreateCarrierUserJunction({
              active: "true",
              ...userFields,
              ...yardFields,
            }),
        })),
        ...plan.reactivate.map((junction) => ({
          reloadYardId: junction.reloadYardId,
          run: () =>
            performUpdateUserReloadCarrier({ ...junction, ...userFields }),
        })),
      ];

      // Each yard is its own row and its own outcome. One failed write must
      // not hide the rows already granted behind a single thrown error: the
      // user is told which yards did not go through, and saving again is safe
      // — the retry re-plans from the fresh junctions, so a grant that did
      // land comes back as alreadyMember rather than a duplicate, and once
      // nothing is left to write a still-pending invitee falls into the
      // resend path above.
      const responses = await Promise.allSettled(
        grants.map((grant) => grant.run()),
      );
      const failed = grants.filter(
        (_, index) => responses[index].status === "rejected",
      );

      if (failed.length) {
        responses
          .filter((response) => response.status === "rejected")
          .forEach((response) =>
            logError(
              "carrierApp:carrierUsers:ValidateCreateButton:onSuccess",
              response.reason,
              false,
            ),
          );
        const reloadYards = (inviterJunctions ?? [])
          .map((junction) => junction.reloadYard)
          .filter(Boolean);
        notify(
          `Could not grant access to ${namesFor(
            failed.map((grant) => grant.reloadYardId),
            reloadYards,
          )}. Please save again to retry.`,
          "warning",
        );
        return;
      }

      // An empty plan is the resend path: the yards were granted by an
      // earlier attempt and only the invitation is left to deliver.
      const isResend = !grants.length;

      // An account that has not registered yet needs the registration invite,
      // whoever it belongs to; one that has signs in with the password it
      // already has. Same invite, two different next steps. A brand-new
      // account always registers — its status was set on the way in, whether
      // or not the create echoed it back, which is why that case is answered
      // before the record is consulted at all.
      const needsRegistration = !isExisting || !hasCarrierLogin(data);
      const emailContent = needsRegistration
        ? emailInviteHTML(
            data.firstName,
            data.email,
            appUser?.firstName,
            businessName,
          )
        : emailCarrierInviteHTML(
            data.firstName,
            data.email,
            appUser?.firstName,
            businessName,
          );

      // The access above is already committed, so a failed send must not read
      // as a failed save. Said as what it is — granted, but not announced —
      // with the way out: saving again re-plans to nothing and lands on the
      // resend confirmation.
      try {
        const response = await API.post("transREST", "/sendEmail", {
          body: {
            to: data.email,
            bcc: ["support@transloads.co"],
            subject: "Your transloads.co Invite Is Waiting",
            template: "emailTemplate.html",
            htmlContent: emailContent,
          },
        });

        if (response.error) throw response.error;
      } catch (error) {
        logError(
          "carrierApp:carrierUsers:ValidateCreateButton:onSuccess",
          error,
          false,
        );
        notify(
          needsRegistration
            ? "Access was granted, but the invitation email could not be sent. Save again to resend it."
            : "Access was granted, but the notification email could not be sent.",
          "warning",
        );
        return;
      }

      notify(
        isResend
          ? "Invitation resent successfully."
          : isExisting
          ? "Transload access granted successfully."
          : "User created successfully.",
      );
      redirect("/carrierUsers");
    } catch (error) {
      logError(
        "carrierApp:carrierUsers:ValidateCreateButton:onSuccess",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError(
      "carrierApp:carrierUsers:ValidateCreateButton:onFailure",
      error,
      notify,
    );
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        {...props}
        submitOnEnter={false}
        undoable={false}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        title="Save Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
